// Chrome shared by every sub-page: left rail, page header, info modal, DQ badge.
import { useEffect, useState } from 'react';
import { dqRules, dqScore, SUB_PAGES } from './helpers.js';

// Left rail — always visible, shows current sub-page.
export function SubNav({ active, onSelect, dqTone }) {
  return (
    <aside className="db-rail">
      <div className="db-rail-head">Billing operations</div>
      {SUB_PAGES.map((p) => (
        <button
          key={p.id}
          className={`db-rail-btn ${active === p.id ? 'active' : ''}`}
          onClick={() => onSelect(p.id)}
          type="button"
        >
          <span className="db-rail-dot" />
          <span>{p.label}</span>
          {p.id === 'data_quality' && dqTone && (
            <span className={`db-pill tone-${dqTone}`} style={{ marginLeft: 'auto' }}>DQ</span>
          )}
        </button>
      ))}
    </aside>
  );
}

// Page header. Title + dynamic subtitle + standard actions.
//   onHome      : optional — go to overview
//   onBack      : optional — pop history
//   onReset     : optional — reset page filters (only shown if provided)
//   onInfo      : optional — open info modal (caller passes its own state)
//   dqBadge     : { score, label, tone, onClick } object
export function PageChrome({
  title,
  subtitle,
  onHome,
  onBack,
  canBack,
  onReset,
  onInfo,
  dqBadge,
}) {
  return (
    <div className="db-chrome">
      <div className="db-chrome-row">
        <div>
          <h2 className="db-chrome-title">{title}</h2>
          {subtitle && <div className="db-chrome-sub">{subtitle}</div>}
        </div>
        <div className="db-chrome-actions">
          {dqBadge && <DqBadge {...dqBadge} />}
          {onHome && (
            <button type="button" className="db-icon-btn" onClick={onHome} title="Go to Overview">
              <span className="db-icon-glyph">⌂</span> Home
            </button>
          )}
          {onBack && (
            <button type="button" className="db-icon-btn" onClick={onBack} disabled={!canBack} title="Back to previous page">
              ← Back
            </button>
          )}
          {onReset && (
            <button type="button" className="db-icon-btn" onClick={onReset} title="Reset page filters">
              ⟲ Reset
            </button>
          )}
          {onInfo && (
            <button type="button" className="db-icon-btn" onClick={onInfo} title="What is this page?">
              <span className="db-icon-glyph">i</span> Info
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// Compact DQ badge in the chrome — clickable, jumps to Data Quality page.
function DqBadge({ score, label, tone, onClick }) {
  return (
    <button type="button" className="db-dq-badge" onClick={onClick} title="Open Data Quality page">
      <span className={`db-dq-badge-score tone-${tone}`}>{score}</span>
      DQ · {label}
    </button>
  );
}

// Info modal — controlled by parent.
export function InfoModal({ open, title, body, onClose }) {
  // Close on Escape.
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onClose]);

  if (!open) return null;
  return (
    <div className="db-modal-backdrop" onClick={onClose}>
      <div className="db-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <div className="db-modal-head">
          <span className="db-icon-glyph">i</span> {title}
        </div>
        <div className="db-modal-body">{body}</div>
        <div className="db-modal-foot">
          <button type="button" className="btn btn-primary" onClick={onClose}>Got it</button>
        </div>
      </div>
    </div>
  );
}

// useInfoModal — convenience hook for sub-pages.
export function useInfoModal() {
  const [open, setOpen] = useState(false);
  return {
    open,
    onOpen: () => setOpen(true),
    onClose: () => setOpen(false),
  };
}

// Compute the DQ badge payload from stats — used by every sub-page.
export function dqBadgeFor(stats, onClick) {
  const score = dqScore(dqRules(stats));
  return { score: score.score, label: score.label, tone: score.tone, onClick };
}
